import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const useAuth = () => {
    const navigate = useNavigate();
    const user_id = localStorage.getItem('user_id');
    const [formData, setFormData] = useState({});
    const [isLoading, setIsLoading] = useState(true);

    // Fonction pour décoder le token JWT
    const decodeToken = (token) => {
        try {
            // Séparer le token en ses 3 parties (header, payload, signature)
            const parts = token.split('.');
            if (parts.length !== 3) {
                throw new Error('Token JWT invalide');
            }

            // Décoder la partie payload (base64url)
            const payload = parts[1];
            // Remplacer les caractères de base64url par base64 standard
            const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
            // Décoder le base64
            const decodedPayload = atob(base64);
            // Parser le JSON
            const parsedPayload = JSON.parse(decodedPayload);

            return parsedPayload;
        } catch (error) {
            console.error('Erreur lors du décodage du token:', error);
            return null;
        }
    };

    // Fonction pour obtenir le temps restant avant expiration du token (en millisecondes)
    const getTokenExpirationTime = (token) => {
        const decoded = decodeToken(token);
        if (!decoded || !decoded.exp) {
            return null;
        }

        // exp est en secondes, convertir en millisecondes
        const expirationTime = decoded.exp * 1000;
        const currentTime = Date.now();
        const timeRemaining = expirationTime - currentTime;

        console.log(`Token expire dans ${Math.floor(timeRemaining / 60000)} minutes`);
        return timeRemaining;
    };

    // Fonction pour rafraîchir le token
    const refreshAccessToken = async () => {
        try {
            const refresh = localStorage.getItem('refresh_token');

            if (!refresh) {
                console.log('Aucun refresh token trouvé');
                return false;
            }

            console.log('Tentative de rafraîchissement du token...');

            const response = await fetch(`${process.env.REACT_APP_HOTE}/refresh-token/`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json"
                },
                body: JSON.stringify({ refresh })
            });

            const data = await response.json();

            if (response.ok && data.access) {
                localStorage.setItem("access_token", data.access);
                console.log('✅ Token rafraîchi avec succès à', new Date().toLocaleTimeString());

                // Si un nouveau refresh token est fourni, le sauvegarder aussi
                if (data.refresh) {
                    localStorage.setItem("refresh_token", data.refresh);
                }

                return true;
            } else {
                console.error('Échec du rafraîchissement:', data);
                return false;
            }
        } catch (error) {
            console.error('Erreur lors du rafraîchissement du token:', error);
            return false;
        }
    };

    // Fonction pour configurer le timeout de rafraîchissement basé sur l'expiration du token
    const setupRefreshTimeout = async () => {
        const token = localStorage.getItem('access_token');

        if (!token) {
            console.log('Token absent, impossible de configurer le refresh');
            return null;
        }

        const timeRemaining = getTokenExpirationTime(token);

        if (timeRemaining === null) {
            console.log('Impossible de déterminer l\'expiration du token, utilisation du timeout par défaut (55 minutes)');
            // Fallback: rafraîchir après 55 minutes
            const timeoutId = setTimeout(async () => {
                const success = await refreshAccessToken();
                if (success) {
                    setupRefreshTimeout();
                } else {
                    console.log('Échec du refresh, déconnexion...');
                    handleLogout();
                }
            }, 55 * 60 * 1000);
            return timeoutId;
        }

        if (timeRemaining <= 0) {
            console.log('Token déjà expiré, tentative de refresh immédiat...');
            const success = await refreshAccessToken();
            if (success) {
                return setupRefreshTimeout();
            } else {
                handleLogout();
                return null;
            }
        }

        // Calculer le délai avant le refresh (5 minutes avant expiration)
        const refreshDelay = Math.max(timeRemaining - 5 * 60 * 1000, 0);
        const refreshDelayMinutes = Math.floor(refreshDelay / 60000);

        console.log(`⏰ Prochain rafraîchissement programmé dans ${refreshDelayMinutes} minutes (${refreshDelayMinutes === 0 ? 'immédiat' : refreshDelayMinutes + ' minutes'})`);

        // Programmer le rafraîchissement
        const timeoutId = setTimeout(async () => {
            console.log('🔄 Lancement du rafraîchissement programmé...');
            const success = await refreshAccessToken();

            if (success) {
                // Reconfigurer le timeout avec le nouveau token
                setupRefreshTimeout();
            } else {
                console.log('❌ Échec du rafraîchissement programmé, déconnexion...');
                handleLogout();
            }
        }, refreshDelay);

        return timeoutId;
    };

    // Ajoutez cette fonction après fetchUtilisateur ou avant les useEffect
    const verifierStatutCompte = () => {
        // Si le compte est inactif
        if (formData.statut && formData.statut !== 'Actif') {
            console.log('⚠️ Compte inactif - Déconnexion');
            handleLogout('Votre compte est inactif. Veuillez contacter l\'administrateur.');
            return false;
        }

        // Si l'utilisateur n'existe plus (formData vide après chargement)
        if (!isLoading && Object.keys(formData).length === 0 && user_id) {
            console.log('⚠️ Utilisateur non trouvé en base - Déconnexion');
            handleLogout('Utilisateur non trouvé. Veuillez vous reconnecter.');
            return false;
        }

        return true;
    };

    // Fonction de déconnexion
    const handleLogout = async () => {
        try {
            const token = localStorage.getItem('access_token');
            const userId = localStorage.getItem('user_id');

            // Appel API de déconnexion (optionnel, ne pas bloquer si erreur)
            if (token && userId) {
                try {
                    await fetch(`${process.env.REACT_APP_HOTE}/deconnexion/`, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${token}`
                        },
                        body: JSON.stringify({ user_id: userId })
                    });
                } catch (error) {
                    console.error('Erreur API déconnexion:', error);
                }
            }

            // Nettoyer localStorage
            const keysToRemove = [
                'access_token', 'refresh_token', 'user_id', 'accesGroupe', 'groupe',
                'redirectAfterLogin'
            ];

            keysToRemove.forEach(key => localStorage.removeItem(key));

            // Redirection avec message
            navigate('/', {
                replace: true,
            });

        } catch (error) {
            console.error('Erreur déconnexion:', error);
            window.location.href = '/';
        }
    };

    // Fonction pour récupérer les informations de l'utilisateur
    const fetchUtilisateur = async () => {
        try {
            const token = localStorage.getItem('access_token');

            // AJOUT: Vérifier si token existe
            if (!token || !user_id) {
                setIsLoading(false);
                return;
            }

            const headers = {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            };

            const response = await fetch(`${process.env.REACT_APP_HOTE}/recup-info-profil/${user_id}/`, {
                method: 'GET',
                headers: headers,
            });

            // AJOUT: Gestion du 404 (utilisateur non trouvé)
            if (response.status === 404) {
                console.log('❌ Utilisateur non trouvé en base de données');
                handleLogout();
                return;
            }

            if (response.status === 401) {
                console.log('🔄 Token invalide, tentative de rafraîchissement...');
                const refreshed = await refreshAccessToken();
                if (refreshed) {
                    return fetchUtilisateur();
                } else {
                    handleLogout();
                    return;
                }
            }

            if (!response.ok) {
                throw new Error(`HTTP Error: ${response.status}`);
            }

            const data = await response.json();
            setFormData(data);

            // AJOUT: Vérifier le statut après récupération
            verifierStatutCompte();

        } catch (error) {
            console.error('Erreur lors de la récupération des données utilisateur', error);
            // AJOUT: Déconnecter en cas d'erreur
            handleLogout();
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (user_id) {
            fetchUtilisateur();
        } else {
            setIsLoading(false);
        }
    }, [user_id]);

    useEffect(() => {
        if (isLoading) return;

        const token = localStorage.getItem('access_token');

        // VÉRIFICATION PRINCIPALE DU TOKEN EN PREMIER
        if (!token) {
            console.log('Token absent');
            localStorage.removeItem('user_id');
            localStorage.removeItem('accesGroupe');
            localStorage.setItem('redirectAfterLogin', window.location.pathname);
            navigate('/', { replace: true });
            return;
        }

        // VÉRIFICATION DE L'EXPIRATION DU TOKEN
        const timeRemaining = getTokenExpirationTime(token);
        if (timeRemaining !== null && timeRemaining <= 0) {
            console.log('Token expiré, tentative de rafraîchissement...');
            refreshAccessToken().then(success => {
                if (!success) {
                    handleLogout();
                }
            });
            return;
        }

        // VÉRIFICATION DU STATUT DU COMPTE
        if (formData.statut && formData.statut !== 'Actif') {
            console.log('Compte non actif');
            handleLogout();
            return;
        }

        // VÉRIFICATION DU NUMÉRO DE TÉLÉPHONE
        if (formData && Object.keys(formData).length > 0) {
            const telEstVide = !formData.tel || formData.tel.trim() === '';
            const telEstNull = formData.tel === null;

            if (telEstVide || telEstNull) {
                console.log('Numéro de téléphone non renseigné, redirection vers la page d\'activation');
                navigate('/activation-page', { replace: true });
                return;
            }
        }

        // Configurer le timeout de rafraîchissement basé sur l'expiration du token
        let timeoutId;

        setupRefreshTimeout().then(id => {
            timeoutId = id;
        });

        // Nettoyage
        return () => {
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
        };
    }, [formData, isLoading, navigate]);

    return { formData, isLoading, handleLogout, refreshAccessToken };
};

export default useAuth;